import React from "react";
import type { ImageTextOverlayCardBlock } from "../../../types/content";
import type { UmbracoLink } from "../../../types/layout";
import { ChevronRightIcon } from "../../icons";
import { resolveHref } from "@/utils/url";
import { getLocale } from "@/utils/locale-store";
import { useLocalizedTexts, t } from "@/utils/translations";
import { imageSizeAttributes } from "@/utils/imageSize";
import { fixedCmsImage, responsiveCmsImage } from "@/utils/cmsImage";
import styles from "./ImageTextOverlayCard.module.css";

/**
 * The card fills its block grid cell, from a third of the grid to a full-bleed
 * 100vw row, and does not know which. A lazy image may say `auto` and let the
 * browser use its laid-out width; a browser without `sizes=auto` gets the
 * widest case.
 */
const BACKGROUND_SIZES = "auto, 100vw";

export interface ImageTextOverlayCardProps {
  /**
   * The image text overlay card block data from Umbraco
   */
  block: ImageTextOverlayCardBlock;

  /**
   * Optional CSS class name
   */
  className?: string;
}

/**
 * Image Text Overlay Card component
 * Promotional card with full background image and overlaid text content
 */
export const ImageTextOverlayCard: React.FC<ImageTextOverlayCardProps> = ({
  block,
  className,
}) => {
  const localizedTexts = useLocalizedTexts();
  const {
    backgroundImage,
    backgroundImageAlt,
    eyebrowImage,
    heading,
    bodyText,
    ctaButton,
    ctaLink,
    overlayOpacity = 40,
    textPosition = "top-left",
    mediaType = "image",
    backgroundVideo,
    wholeCardClickable = false,
    isMainTitle = false,
  } = block;

  if (!backgroundImage || !heading) {
    return null;
  }

  const containerClasses = [styles.card, className].filter(Boolean).join(" ");

  // CMS pages have no page title of their own, so the editor marks one card's
  // heading as the page h1. The tag changes, the card-heading styling does not.
  const HeadingTag = isMainTitle ? "h1" : "h3";

  // Calculate overlay opacity as CSS custom property
  const overlayOpacityValue = overlayOpacity / 100;

  // When enabled, the whole card links to the CTA button destination,
  // falling back to the CTA link. Null means the card is not clickable as a whole.
  const cardLinkTarget = wholeCardClickable ? (ctaButton ?? ctaLink) : null;

  const hrefFor = (link: UmbracoLink) =>
    resolveHref(link, getLocale().language);

  const hasCtaText = (link: UmbracoLink) => Boolean(link.name?.trim());

  // With a whole-card link the CTAs are decorative labels, so one without its
  // own text has nothing worth showing - the generic "Learn more" fallback
  // only applies when the CTA is a real, independently clickable link.
  const ctaLabel = (link: UmbracoLink) =>
    cardLinkTarget
      ? link.name
      : link.name ||
        t(localizedTexts, "Tracker.Generic.LearnMore", "Learn more");

  const showCtaButton =
    !!ctaButton && (!cardLinkTarget || hasCtaText(ctaButton));
  const showCtaLink = !!ctaLink && (!cardLinkTarget || hasCtaText(ctaLink));

  // A whole-card link must not contain nested anchors, so the CTAs keep their
  // styling but render as plain labels when the card itself is the link.
  const renderCta = (
    link: UmbracoLink,
    ctaClassName: string | undefined,
    children: React.ReactNode
  ) =>
    cardLinkTarget ? (
      <span className={ctaClassName}>{children}</span>
    ) : (
      <a
        href={hrefFor(link)}
        target={link.target || "_self"}
        className={ctaClassName}
        rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
      >
        {children}
      </a>
    );

  const cardInner = (
    <>
      {/* Background Media */}
      <div className={styles.imageWrapper} data-card-surface aria-hidden="true">
        {mediaType === "video" && backgroundVideo ? (
          <video
            ref={(el) => {
              if (el) {
                el.muted = true;
                el.defaultMuted = true;
                el.play().catch(() => {});
              }
            }}
            autoPlay
            loop
            muted
            playsInline
            poster={backgroundImage?.url}
            className={styles.backgroundVideo}
            src={backgroundVideo.url}
          >
            <track kind="captions" />
          </video>
        ) : (
          <img
            {...responsiveCmsImage(
              backgroundImage.url,
              BACKGROUND_SIZES,
              backgroundImage.width
            )}
            alt={backgroundImageAlt || ""}
            className={styles.backgroundImage}
            loading="lazy"
          />
        )}
        <div className={styles.overlay} />
      </div>

      {/* Content */}
      <div className={styles.content} data-text-position={textPosition}>
        {eyebrowImage && (
          <img
            // max-height 32px on a phone, 40px from 768px.
            {...fixedCmsImage(eyebrowImage.url, { height: 40 })}
            alt={eyebrowImage.altText || ""}
            className={styles.eyebrowImage}
            {...imageSizeAttributes(eyebrowImage)}
          />
        )}

        <HeadingTag className={styles.heading}>{heading}</HeadingTag>

        {bodyText && <p className={styles.bodyText}>{bodyText}</p>}

        {(showCtaButton || showCtaLink) && (
          <div className={styles.ctaWrapper}>
            {ctaButton &&
              showCtaButton &&
              renderCta(ctaButton, styles.ctaButton, ctaLabel(ctaButton))}

            {ctaLink &&
              showCtaLink &&
              renderCta(
                ctaLink,
                styles.ctaLink,
                <>
                  <span>{ctaLabel(ctaLink)}</span>
                  <ChevronRightIcon className={styles.ctaIcon} />
                </>
              )}
          </div>
        )}
      </div>
    </>
  );

  return (
    <article
      className={containerClasses}
      data-component="image-text-overlay-card"
      data-text-position={textPosition}
      data-whole-card-clickable={cardLinkTarget ? "true" : undefined}
      style={
        {
          "--card-overlay-opacity": overlayOpacityValue,
        } as React.CSSProperties
      }
    >
      {cardLinkTarget ? (
        <a
          href={hrefFor(cardLinkTarget)}
          target={cardLinkTarget.target || "_self"}
          className={styles.cardLink}
          rel={
            cardLinkTarget.target === "_blank"
              ? "noopener noreferrer"
              : undefined
          }
          aria-label={heading}
        >
          {cardInner}
        </a>
      ) : (
        cardInner
      )}
    </article>
  );
};

export default ImageTextOverlayCard;
